import { useTerminalsStateRE } from "@/States/Terminals";
import { useCommonEntitiesStore } from "@/States/commonEntities";
import { getFlatDownstreamData } from "@/Utils/downstreamHandlingUtils";
import { ExportButton } from "@/components/ExportButton";
import { defaultTimeFilter } from "@/components/v2/DatePicker/constants";
import { useConfigService } from "@/pages/configuration/useConfigService";
import { TAB_LABELS } from "@/pages/reporting_v2/constants";
import { ReportingPageContext } from "@/pages/reporting_v2/context";
import {
  REPORTING_TAB_TYPES,
  REPORTING_VIEW_TYPES,
} from "@/pages/reporting_v2/types";
import { useTrans } from "@/translations";

import { useCallback, useContext, useMemo, useState } from "react";

import { ReportingTableContext } from "../../../context";
import type { TableRow, WasteColumn } from "../../../types";
import { buildTotalsRow } from "../../dataFunctions/rowsFunction";
import type { ReportingExportProps } from "./utils/common";
import { exportReportingCsv } from "./utils/csv";
import { exportReportingExcel } from "./utils/excel";

type Props = {
  rows: TableRow[];
  selectedRows: Record<string, boolean>;
  wasteColumns: WasteColumn[];
};

export const ReportingExportButton: React.FC<Props> = ({
  rows,
  selectedRows,
  wasteColumns,
}) => {
  const { t } = useTrans();
  const { currentTerminal } = useTerminalsStateRE();
  const { viewType, tabType, timeFilter, selectorLabel } =
    useContext(ReportingPageContext);
  const {
    nrSelected,
    nrTotal,
    isLoading: isLoadingData,
    showZones,
    unit,
  } = useContext(ReportingTableContext);

  const { wasteTypeConfig } = useConfigService();
  const { downstreamHandling } = useCommonEntitiesStore();

  const [isExporting, setIsExporting] = useState(false);

  const nrSelectedValue = useMemo(
    () => (tabType === REPORTING_TAB_TYPES.INDIVIDUAL ? nrSelected : 0),
    [nrSelected, tabType],
  );

  const isPortfolioView = useMemo(
    () => viewType === REPORTING_VIEW_TYPES.PORTFOLIO,
    [viewType],
  );

  const isIndividualTab = useMemo(
    () => tabType === REPORTING_TAB_TYPES.INDIVIDUAL,
    [tabType],
  );

  const flatDownstreamData = useMemo(
    () => getFlatDownstreamData(downstreamHandling || []),
    [downstreamHandling],
  );

  const getExportProps = useCallback((): ReportingExportProps => {
    const selected = rows.filter((row) => selectedRows[row.key]);
    const exportRows = isIndividualTab && selected.length ? selected : rows;
    const scope = isPortfolioView
      ? t("reportingPage:viewTypePortfolioLabel")
      : currentTerminal?.name;

    return {
      rows: exportRows,
      totalsRow: buildTotalsRow(exportRows, wasteColumns),
      wasteColumns,
      unit,
      isIndividual: isIndividualTab,
      isPortfolio: isPortfolioView,
      showZones: !isPortfolioView && showZones,
      nameLabel: isPortfolioView
        ? "reportingPage:reportingColumnBuilding"
        : "reportingPage:reportingColumnTenant",
      title: t(TAB_LABELS[viewType][tabType]),
      selection: [
        scope,
        selectorLabel,
        exportRows.length < rows.length &&
          t("reportingPage:footerSelected", { nrRows: exportRows.length }),
      ]
        .filter(Boolean)
        .join(" · "),
      timeframe: timeFilter || defaultTimeFilter,
      wasteTypeConfig,
      flatDownstreamData,
    };
  }, [
    rows,
    selectedRows,
    isIndividualTab,
    isPortfolioView,
    currentTerminal?.name,
    wasteColumns,
    unit,
    showZones,
    t,
    viewType,
    tabType,
    selectorLabel,
    timeFilter,
    wasteTypeConfig,
    flatDownstreamData,
  ]);

  const runExport = useCallback(
    async (exportFn: (props: ReportingExportProps) => Promise<void>) => {
      setIsExporting(true);
      try {
        await exportFn(getExportProps());
      } finally {
        setIsExporting(false);
      }
    },
    [getExportProps],
  );

  const onExportExcel = useCallback(
    () => runExport(exportReportingExcel),
    [runExport],
  );
  const onExportCsv = useCallback(
    () => runExport(exportReportingCsv),
    [runExport],
  );

  const isLoadingExporting = useMemo(
    () => isLoadingData || isExporting,
    [isExporting, isLoadingData],
  );

  return (
    <ExportButton
      nrSelected={nrSelectedValue}
      nrTotal={nrTotal}
      areAllSelected={nrSelectedValue === nrTotal}
      exportExcelFunction={onExportExcel}
      exportCsvFunction={onExportCsv}
      isLoading={isLoadingExporting}
      disabled={!isLoadingExporting && nrTotal === 0}
      disabledText={t("actions:exportAll")}
    />
  );
};
